import React, { useEffect, useState, useCallback } from 'react';
import { useSessionStorage, useFetch } from 'hooks';
import { hasFeatureFlags } from 'utils/features';

import { routes } from 'constants/config';
import { Modal, ButtonGroup, Button, Icon } from 'ui';
import ModalContainer from 'components/shared/modal/modal_container';

const LessonMaxRetakeModal = () => {
  const [shown, setShown] = useState(false);
  const [remainingAttempts, setRemainingAttempts] = useState(1);
  const lessonId = window.location.pathname.split('lesson/')[1].split('-')[0];
  const sessionStorageKey = `maxAttemptsModalShown_${lessonId}`;
  const hasModalBeenShown = useSessionStorage(sessionStorageKey);
  const maximumAttemptsEnabled = hasFeatureFlags(['learning_maximum_attempts']);
  const { data: attempts } = useFetch(
    routes.LEARNING.MAXIMUM_ATTEMPTS(lessonId),
  );
  const startedAtLimit = 60000;

  const toggleModal = () => {
    if (remainingAttempts > 0 || (remainingAttempts < 1 && !shown)) {
      setShown(!shown);
    }
  };

  const checkSessionStorage = useCallback(() => {
    const expiredAt = sessionStorage.getItem(`${sessionStorageKey}ExpiresAt`);
    if (expiredAt) {
      if (Date.now() > new Date(expiredAt)) {
        sessionStorage.removeItem(sessionStorageKey);
        sessionStorage.removeItem(`${sessionStorageKey}ExpiresAt`);
      } else {
        setTimeout(checkSessionStorage, 1000);
      }
    }
  }, [sessionStorageKey]);

  checkSessionStorage();

  useEffect(() => {
    const startedAtLimitDate = new Date(Date.now() - startedAtLimit);
    if (attempts) {
      const attemptsRemaining = Math.max(
        attempts.max_attempts - attempts.count,
        0,
      );
      setRemainingAttempts(attemptsRemaining);
      if (
        new Date(attempts.started_at) > startedAtLimitDate &&
        hasModalBeenShown !== 'true' &&
        remainingAttempts > 0
      ) {
        setShown(true);
        sessionStorage.setItem(sessionStorageKey, true);
        sessionStorage.setItem(
          `${sessionStorageKey}ExpiresAt`,
          new Date(Date.now() + startedAtLimit).toISOString(),
        );
        checkSessionStorage();
      }
      if (remainingAttempts < 1) {
        setShown(true);
      }
    }
  }, [
    attempts,
    hasModalBeenShown,
    remainingAttempts,
    sessionStorageKey,
    checkSessionStorage,
  ]);

  return (
    <>
      {maximumAttemptsEnabled && (
        <Modal
          shown={shown}
          onDismiss={toggleModal}
          className="bg-gray-lightest Modal-container overflow-visible"
        >
          {remainingAttempts > 0 && (
            <div
              role="button"
              tabIndex={0}
              aria-label={t('common.close')}
              className="AssignModal-closeButton"
              onClick={toggleModal}
              onKeyDown={e => e.key === 'Enter' && toggleModal()}
            >
              <Icon name="error" />
            </div>
          )}
          <ModalContainer onEscape={toggleModal}>
            <Modal.Content toggleModal={toggleModal} className="d-f fd-c">
              <div className="Modal-body pb-0">
                <div className="Modal-body-content">
                  <div className="Modal-body-header fz-24 mb-12">
                    {t('learning.lessons.event.attempts_remaining', {
                      attempts_remaining: remainingAttempts,
                    })}
                  </div>
                  <div className="Modal-body-text fz-16">
                    {remainingAttempts > 0
                      ? t('learning.lessons.event.attempts_action')
                      : t('learning.lessons.event.max_attempts_reached')}
                  </div>
                </div>
              </div>
            </Modal.Content>
            <ButtonGroup alignEnd className="pb-32 pr-32">
              {remainingAttempts > 0 && (
                <Button className="Button--customColor" onClick={toggleModal}>
                  {t('common.continue')}
                </Button>
              )}
              {remainingAttempts < 1 && (
                <Button
                  className="Button--customColor"
                  onClick={() => {
                    window.history.go(-1);
                    return false;
                  }}
                >
                  {t('common.go_back')}
                </Button>
              )}
            </ButtonGroup>
          </ModalContainer>
        </Modal>
      )}
    </>
  );
};

export default LessonMaxRetakeModal;
